// Costs (WP5.2): roll every standard cost up through the recipes (a person's cost is never
// overwritten) and see the coverage — "computed for X of Y; missing: …", never a silent 0
// (lesson 12) — then each recipe's cost, which opens the roll-up line by line.

import type { RecipeSummary } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Button,
  createColumnHelper,
  DataTable,
  EmptyState,
  Input,
  Money,
  PageHeader,
  StatusBadge,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { RefreshCw, Search, SearchX, Sigma, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Panel,
  ProblemNote,
  RecipeViews,
  useCan,
  useSearchText,
} from '../../../../production/parts.js';
import {
  costsSearchSchema,
  invalidateProduction,
  recipesQuery,
  runCostRollup,
} from '../../../../production/queries.js';
import {
  COST_SOURCE_LABEL,
  costChangeText,
  coverageSentence,
  coverageStatus,
  refusalText,
  sentence,
  unitCostText,
} from '../../../../production/words.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { LoadMoreFooter } from '../../../../stock/components.js';
import { clean } from '../../../../stock/queries.js';
import { actionProblem } from '../../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/recipes/costs/')({
  validateSearch: costsSearchSchema,
  component: CostsPage,
});

type RollupResult = Awaited<ReturnType<typeof runCostRollup>>;

function RollupPanel() {
  const queryClient = useQueryClient();
  const canRun = useCan('catalogue.edit', true);
  const [result, setResult] = useState<RollupResult | null>(null);
  const run = useMutation({
    mutationFn: () => runCostRollup(),
    onSuccess: async (r) => {
      setResult(r);
      await invalidateProduction(queryClient);
    },
  });
  const failed = run.isError ? actionProblem(run.error, 'The costs weren’t rolled up') : null;
  const coverage = result ? coverageStatus(result.coverage) : null;
  return (
    <Panel
      id="rollup"
      title="Roll up every cost"
      description="Works out each made product’s standard cost from its recipe, however deeply nested. A cost a person set is kept."
      actions={
        canRun ? (
          <Button
            icon={<RefreshCw aria-hidden />}
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            Roll up costs now
          </Button>
        ) : (
          <p className="max-w-64 text-sm text-muted">
            Rolling up needs the “edit the catalogue” permission and every branch.
          </p>
        )
      }
      className="mb-6"
    >
      {failed && <ProblemNote problem={failed} />}
      {result && coverage && (
        <div className="flex flex-col gap-3" aria-live="polite">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={coverage.status}>{coverage.label}</StatusBadge>
            <p className="text-body text-fg" data-testid="rollup-summary">
              {coverageSentence(result.coverage)}
            </p>
          </div>
          <dl className="flex flex-wrap gap-x-8 gap-y-2 text-body">
            {(
              [
                ['Costs changed', result.updated],
                ['A person’s cost kept', result.kept_person_cost],
                ['Unchanged', result.unchanged],
              ] as const
            ).map(([label, n]) => (
              <div key={label} className="flex flex-col">
                <dt className="text-sm text-muted">{label}</dt>
                <dd className="font-display text-xl font-semibold tabular-nums text-fg">
                  {formatCount(n)}
                </dd>
              </div>
            ))}
          </dl>
          {result.changes.length > 0 && (
            // UI5A-11: which costs changed, and by how much.
            // Capped, so the figures sit near their names at 1440 (UI5A-208).
            <div className="flex max-w-2xl flex-col gap-1.5">
              <h3 className="text-sm font-semibold text-fg">What changed</h3>
              <ul aria-label="Costs that changed" className="flex flex-col gap-1">
                {result.changes.slice(0, 20).map((c) => (
                  <li
                    key={c.product.id}
                    className="flex flex-wrap items-baseline justify-between gap-x-4 text-body"
                  >
                    <Link
                      to="/recipes/costs/$productId"
                      params={{ productId: c.product.id }}
                      className="font-medium text-primary-text underline-offset-4 hover:underline"
                    >
                      {c.product.name}
                    </Link>
                    <span className="tabular-nums text-fg">
                      {costChangeText(c.before_cents, c.after_cents, c.product.base_uom)}
                    </span>
                  </li>
                ))}
              </ul>
              {result.changes.length > 20 && (
                <p className="text-sm text-muted">
                  And {formatCount(result.changes.length - 20)} more.
                </p>
              )}
            </div>
          )}
          {result.updated === 0 && (
            <p className="text-sm text-muted">
              No stored cost changed: they already matched the recipes.
            </p>
          )}
          {result.coverage.missing.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <h3 className="text-sm font-semibold text-fg">No cost yet</h3>
              <ul aria-label="Products without a cost" className="flex flex-col gap-1">
                {result.coverage.missing.slice(0, 20).map((m) => (
                  <li key={m.product.id} className="text-body">
                    <Link
                      to="/recipes/costs/$productId"
                      params={{ productId: m.product.id }}
                      className="font-medium text-primary-text underline-offset-4 hover:underline"
                    >
                      {m.product.name}
                    </Link>
                    <span className="text-muted">: {sentence(refusalText(m.gap.message))}</span>
                  </li>
                ))}
              </ul>
              {result.coverage.missing.length > 20 && (
                <p className="text-sm text-muted">
                  And {formatCount(result.coverage.missing.length - 20)} more.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {!result && !failed && (
        <p className="text-sm text-muted">
          Signing off a recipe rolls its costs up by itself. Roll up everything after changing
          purchase costs, or to check the coverage.
        </p>
      )}
    </Panel>
  );
}

function columns(): ColumnDef<RecipeSummary, unknown>[] {
  const col = createColumnHelper<RecipeSummary>();
  return [
    col.display({
      id: 'product',
      header: 'Product',
      meta: { width: 'min-w-52' },
      cell: ({ row }) => (
        <Link
          to="/recipes/costs/$productId"
          params={{ productId: row.original.product.id }}
          className="font-medium text-fg hover:underline"
        >
          {row.original.product.name}
        </Link>
      ),
    }),
    col.display({
      id: 'unit',
      header: 'Per unit',
      meta: { align: 'right' },
      cell: ({ row }) =>
        row.original.standard_cost_cents === null ? (
          <span className="whitespace-nowrap text-sm text-muted">Not worked out</span>
        ) : (
          <span className="whitespace-nowrap text-fg">
            {unitCostText(row.original.standard_cost_cents, row.original.product.base_uom)}
          </span>
        ),
    }),
    col.display({
      id: 'batch',
      header: 'One batch',
      meta: { align: 'right' },
      // Summed line by line by the server, as on the cost page (UI5A-8).
      cell: ({ row }) =>
        row.original.batch_cost_cents === null ? (
          <span className="text-muted">—</span>
        ) : (
          <Money cents={row.original.batch_cost_cents} />
        ),
    }),
    col.display({
      id: 'state',
      header: 'Coverage',
      cell: ({ row }) => {
        const r = row.original;
        if (!r.costed_version) return <span className="text-sm text-muted">No live version</span>;
        return (
          <span className="flex flex-col items-start gap-0.5">
            {r.cost_gap ? (
              <StatusBadge status="warning">Cost missing</StatusBadge>
            ) : (
              <StatusBadge status="success">Every cost computed</StatusBadge>
            )}
            <span className="max-w-64 text-xs text-muted">
              {r.cost_gap
                ? sentence(refusalText(r.cost_gap.message))
                : r.costed_version.status === 'draft'
                  ? `From draft version ${r.costed_version.version_no}, not signed off`
                  : `Version ${r.costed_version.version_no}, in use`}
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'source',
      header: 'Where it comes from',
      meta: { className: 'hidden md:table-cell' },
      cell: ({ row }) =>
        row.original.cost_source ? (
          <span className="text-sm text-fg">{COST_SOURCE_LABEL[row.original.cost_source]}</span>
        ) : (
          <span className="text-sm text-muted">—</span>
        ),
    }),
  ];
}

function CostsPage() {
  useDocumentTitle('Costs');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [text, setText] = useSearchText(
    search.q,
    (q) => void navigate({ search: (prev) => clean({ ...prev, q }), replace: true }),
  );
  const list = useInfiniteQuery({
    ...recipesQuery({ q: search.q }),
    placeholderData: keepPreviousData,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

  return (
    <>
      <PageHeader
        title="Recipes"
        description="Standard costs: what a product should cost to make, from its recipe and its ingredients’ costs."
      />
      <RecipeViews current="costs" />
      <RollupPanel />

      <section aria-labelledby="each-cost" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="each-cost" className="font-display text-lg font-semibold text-fg">
            Cost of each recipe
          </h2>
          <div className="relative w-full max-w-64" role="search" aria-label="Cost search">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              type="search"
              aria-label="Search costs by product"
              placeholder="Search by product name"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        {list.isError && !list.data ? (
          <ErrorPanel
            error={list.error}
            titleAs="h2"
            what="The costs"
            onRetry={() => void list.refetch()}
          />
        ) : (
          <DataTable
            label="Recipe costs"
            columns={columns()}
            data={items}
            loading={list.isPending}
            filterable={false}
            getRowId={(r) => r.id}
            onRowActivate={(r) =>
              void navigate({
                to: '/recipes/costs/$productId',
                params: { productId: r.product.id },
              })
            }
            empty={
              search.q ? (
                <EmptyState
                  compact
                  icon={SearchX}
                  title="No recipes match"
                  description={`No recipe for a product called “${search.q}”.`}
                  action={
                    <Button
                      variant="secondary"
                      icon={<X aria-hidden />}
                      onClick={() => {
                        setText('');
                        void navigate({ search: {} });
                      }}
                    >
                      Clear the search
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  compact
                  icon={Sigma}
                  title="No recipes to cost yet"
                  description="Costs roll up through recipes. Import your catalogue or write a recipe first."
                  action={
                    <Button variant="secondary" asChild>
                      <Link to="/recipes">Go to recipes</Link>
                    </Button>
                  }
                />
              )
            }
            footer={
              !list.isPending && items.length > 0 ? (
                <LoadMoreFooter
                  loaded={items.length}
                  total={list.data?.pages[0]?.total}
                  noun={['recipe', 'recipes']}
                  hasMore={list.hasNextPage}
                  loading={list.isFetchingNextPage}
                  onMore={() => void list.fetchNextPage()}
                />
              ) : undefined
            }
          />
        )}
      </section>
    </>
  );
}
